import React, { useRef, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Container, Grid, Link, Select, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { gtm } from '../../services/gtm';
import { CYBER_CONFIG } from '../../constants';

interface RequestFormProps {
  componentLocation?: 'splash' | 'dashboard' | string;
}

// Minimal country list. Extend as needed.
const countryList = CYBER_CONFIG.requestForm.country;
const usStates = CYBER_CONFIG.requestForm.states;
const roleList = CYBER_CONFIG.requestForm.roleList;
const requestList = CYBER_CONFIG.requestForm.requestList;

const INITIAL_FORM_STATE = {
  organization: '',
  name: '',
  role: '',
  roleComments: '',
  email: '',
  state: '',
  phone: '',
  request: '',
  requestComments: '',
  country: CYBER_CONFIG.requestForm.defaultCountry || '',
};

export const RequestForm: React.FC<RequestFormProps> = ({ componentLocation = 'dashboard' }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isDashboard = componentLocation === 'dashboard';

  const [form, setForm] = useState(INITIAL_FORM_STATE);

  const [showState, setShowState] = useState(true);
  const [initializeForm, setInitializeForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const onDirty = () => {
    if (!initializeForm) {
      setInitializeForm(true);
      gtm.formStep('More Information', 'Form Started', 1, false, false);
    }
  };

  const onChangeField = (label: string, value: any) => {
    onDirty();
    gtm.formEvent(`Changed ${label}${value ? ': ' + value : ''}`, 'More Information');
  };

  const handleResetForm = () => {
    setSubmitted(false);
    setLoading(false);
    //reset form fields
    setForm(INITIAL_FORM_STATE);
  };

  const handleCountryChange = (country: string) => {
    setForm(prev => ({ ...prev, country }));
    setShowState(country === CYBER_CONFIG.requestForm.defaultCountry);
    if (country !== CYBER_CONFIG.requestForm.defaultCountry) {
      setForm(prev => ({ ...prev, state: '' }));
    }
    onChangeField('Country', country);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    onDirty();
    setLoading(true);
    // We let the browser submit to Salesforce in hidden iframe
    // When submission completes, we just show a thank you after a short delay
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      gtm.formStep('More Information', 'Form Submitted', 2, true, {
        organization : form.organization,
        role: form.role,
        roleComments: form.roleComments,
        state: form.state,
        request: form.request,
        requestComments: form.requestComments,
        country: form.country
      });
    }, 3000);
  };

  const bgColor = theme.custom?.navy || theme.palette.primary.dark;

  return (
    <Box
      sx={{
        ...(isDashboard
          ? {
              width: '100%',
              position: 'relative',
            }
          : {
              width: '100vw',
              position: 'relative',
              left: '50%',
              right: '50%',
              marginLeft: '-50vw',
              marginRight: '-50vw',
            }),
        backgroundColor: bgColor,
        color: 'white',
        py: 4,
      }}
    >
      <Container maxWidth={isDashboard ? false : 'lg'} disableGutters={isDashboard}>
        {/* Hidden target iframe */}
        <iframe title="request-target" name="requestIframe" ref={iframeRef} style={{ display: 'none' }} />

        {/* Loading state */}
        {loading && !submitted && (
          <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 160 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" color='primary.contrastText' sx={{ mb: 2, textAlign: 'center' }}>
                Please wait while we submit your request
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress color="inherit" />
              </Box>
            </Grid>
          </Grid>
        )}

        {/* Thank you state */}
        {submitted && (
          <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 160 }}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" color='primary.contrastText' sx={{ textAlign: 'center', mb: 1 }}>
                Thank you for your interest
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1" color='primary.contrastText' sx={{ textAlign: 'center' }}>
                A Chubb Cyber specialist will be in touch with you soon.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color='primary.contrastText' sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                    <Link
                    component="button"
                    onClick={() => handleResetForm()}
                    data-cta="Terms & Conditions"
                    underline="none"
                    sx={{
                        color: 'primary.contrastText',
                        textDecoration: 'none',
                        textTransform: 'none',
                        p: 0,
                        minWidth: 'unset',
                        fontSize: 'inherit',
                        verticalAlign: 'baseline',
                        lineHeight: 'inherit',
                        display: 'inline',
                        boxShadow: 'inset 0 -4px 0 var(--mui-palette-primary-main);',
                        transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                            backgroundColor: theme.palette.primary.main,
                            boxShadow: 'inset 0 -4px 0 var(--mui-palette-primary-main);',
                        },
                    }}
                >
                    I have another question
                </Link>
              </Typography>
            </Grid>
          </Grid>
        )}

        {/* Form state */}
        {!loading && !submitted && (
          <Box 
            component="form"
            id="contactUs"
            action="https://webto.salesforce.com/servlet/servlet.WebToCase?encoding=UTF-8"
            method="POST"
            target="requestIframe"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="orgid" value="00D30000000026K" />
            <Grid container spacing={2} sx={{ mx: isDashboard ? 2 : 0}}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5" color='primary.contrastText'>If you would like more information, please complete this form.</Typography>
              </Grid>

              {/* Row 1: Name, Country (Autocomplete), Role (Autocomplete) */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  required
                  id="name"
                  name="name"
                  label="Name"
                  value={form.name}
                  onChange={(e) => { setForm(prev => ({ ...prev, name: e.target.value })); onChangeField('Contact Name', false); }}
                  onBlur={(e) => onChangeField('Contact Name', false)}
                  variant="filled"
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                    inputLabel: { sx: { color: 'white' } }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  options={countryList}
                  value={form.country}
                  onChange={(_, val) => handleCountryChange(val || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      variant="filled"
                      sx={{ mb: 2 }}
                      slotProps={{
                        input: { ...params.InputProps, sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                        inputLabel: { sx: { color: 'white' } }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': { color: 'white' },
                    '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                  }}
                />
                {/* Hidden input to submit selected country */}
                <input type="hidden" name="00N4w00000EqPG6" value={form.country} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  options={roleList}
                  getOptionLabel={(o) => (o?.text ?? '')}
                  value={roleList.find(r => r.name === form.role) || null}
                  onChange={(_, val) => { setForm(prev => ({ ...prev, role: val?.name || '' })); onChangeField('Role', val?.name || ''); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role"
                      variant="filled"
                      sx={{ mb: 2 }}
                      slotProps={{
                        input: { ...params.InputProps, sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                        inputLabel: { sx: { color: 'white' } }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': { color: 'white' },
                    '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                  }}
                />
                {/* Hidden input for Salesforce role field */}
                <input type="hidden" name="00N4w00000EqPGC" value={form.role} />
              </Grid>

              {/* Show Role - Other below if selected */}
              {form.role === 'Other' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="00N4w00000EqPGB"
                    name="00N4w00000EqPGB"
                    label="Please specifiy your role (Optional)"
                    value={form.roleComments}
                    onChange={(e) => { setForm(prev => ({ ...prev, roleComments: e.target.value })); onChangeField('Other Role', form.roleComments); }}
                    onBlur={() => onChangeField('Other Role', form.roleComments)}
                    variant="filled"
                    sx={{ mb: 2 }}
                    slotProps={{
                      input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                      inputLabel: { sx: { color: 'white' } }
                    }}
                  />
                </Grid>
              )}

              {/* Row 2: Email, State (Autocomplete), Subject (Autocomplete) */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  id="email"
                  name="email"
                  label="Business Email"
                  value={form.email}
                  onChange={(e) => { setForm(prev => ({ ...prev, email: e.target.value })); onChangeField('Email', false); }}
                  onBlur={(e) => onChangeField('Email', false)}
                  variant="filled"
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                    inputLabel: { sx: { color: 'white' } }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                {showState && (
                  <>
                    <Autocomplete
                      options={usStates}
                      getOptionLabel={(o) => (o?.name ?? '')}
                      value={usStates.find(s => s.name === form.state) || null}
                      onChange={(_, val) => { setForm(prev => ({ ...prev, state: val?.name || '' })); onChangeField('State', val?.name || ''); }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="State"
                          variant="filled"
                          sx={{ mb: 2 }}
                          slotProps={{
                            input: { ...params.InputProps, sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                            inputLabel: { sx: { color: 'white' } }
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-popupIndicator': { color: 'white' },
                        '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                      }}
                    />
                    {/* Hidden input for Salesforce state field */}
                    <input type="hidden" name="00N4w00000EqPGD" value={form.state} />
                  </>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  options={requestList}
                  getOptionLabel={(o) => (o?.text ?? '')}
                  value={requestList.find(r => r.name === form.request) || null}
                  onChange={(_, val) => { setForm(prev => ({ ...prev, request: val?.name || '' })); onChangeField('Request', val?.name || ''); }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Request"
                      variant="filled"
                      sx={{ mb: 2 }}
                      slotProps={{
                        input: { ...params.InputProps, sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                        inputLabel: { sx: { color: 'white' } }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-popupIndicator': { color: 'white' },
                    '& .MuiAutocomplete-clearIndicator': { color: 'white' },
                  }}
                />
                {/* Hidden input for Salesforce subject field */}
                <input type="hidden" name="subject" value={form.request} />
              </Grid>

              {/* Description if Other request selected */}
              {form.request === 'Other' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    id="description"
                    name="description"
                    multiline
                    minRows={4}
                    label="Comment and Questions"
                    value={form.requestComments}
                    onChange={(e) => { setForm(prev => ({ ...prev, requestComments: e.target.value })); onChangeField('Other Request', form.requestComments); }}
                    variant="filled"
                    sx={{ mb: 2 }}
                    slotProps={{
                      input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                      inputLabel: { sx: { color: 'white' } }
                    }}
                  />
                </Grid>
              )}

              {/* Row 3: Organization, Phone */}
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  id="company"
                  name="company"
                  label="Organization"
                  value={form.organization}
                  onChange={(e) => { setForm(prev => ({ ...prev, organization: e.target.value })); onChangeField('Company', form.organization); }}
                  variant="filled"
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                    inputLabel: { sx: { color: 'white' } }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  id="phone"
                  name="phone"
                  label="Phone (Optional)"
                  value={form.phone}
                  onChange={(e) => { setForm(prev => ({ ...prev, phone: e.target.value })); onChangeField('Phone', false); }}
                  onBlur={(e) => onChangeField('Phone', false)}
                  variant="filled"
                  sx={{ mb: 2 }}
                  slotProps={{
                    input: { sx: { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' } },
                    inputLabel: { sx: { color: 'white' } }
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button 
                    type="submit"
                    variant="contained"
                    color="primary"
                    size='large'
                    sx={{ textTransform: 'none', fontSize: '1rem' }}
                >
                  Submit
                </Button>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" color='primary.contrastText'>
                  If you are a California resident, please{' '}
                  <Link
                    href="https://www.chubb.com/us-en/online-privacy-policy.html#notice-at-collection"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-download="CCPA At Collection Notice_General"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      display: 'inline',
                      position: 'relative',
                      fontSize: 'inherit',
                      lineHeight: 'inherit',
                      cursor: 'pointer',
                      boxShadow: 'inset 0 -4px 0 var(--mui-palette-primary-main);',
                      transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        boxShadow: 'inset 0 -4px 0 var(--mui-palette-primary-main);',
                      },
                    }}
                  >
                    read our Notice at Collection.
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default RequestForm;
